/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ReactNode,
  useEffect,
  useState
} from 'react';
import {
  Grid,
  LinearProgress,
  Typography
} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import classnames from 'classnames';
import filter from 'lodash.filter';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import isFunction from 'lodash.isfunction';
import isNil from 'lodash.isnil';
import isUndefined from 'lodash.isundefined';
import {WorkBook} from 'xlsx/types';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {
  Entity,
  GenericObject
} from '@ion/core/src/types/entities';
import {VoidEventHandler} from '@ion/core/src/types/general';
import {
  BulkOperationResults,
  ExtendedResults
} from '@ion/core/src/types/services';
import {
  DownloadTemplate,
  DropsheetOptions,
  TemplateColumn,
  TemplateOptions
} from '@ion/core/src/types/templates';
import {idMap} from '@ion/core/src/utils/ObjectUtil';
import {isEmpty as isStringEmpty} from '@ion/core/src/utils/StringUtil';
import {
  normalizeAttributes,
  normalizeHeaders,
  process
} from '@ion/core/src/xlsx/ExcelHelpers';
import {exporter} from '@ion/core/src/xlsx/exporter';
import {dropsheet} from '@ion/core/src/xlsx/importer';
import {DownloadTemplateDeviceTypes} from '@ion/core/src/xlsx/TemplateHelpers';
import CloseButton from '../components/buttons/CloseButton';
import {IconButton} from '../components/buttons/IconButton';
import {CancelButton} from '../editors/controls/CancelButton';
import {SaveButton} from '../editors/controls/SaveButton';
import {VisionDeviceType} from '../editors/DeviceEditorUtils';
import {getClasses} from '../editors/editorStyles';
import {useExtendStyles} from '../hooks/useExtendStyles';
import {FileIcon} from '../icons/FileIcon';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import {TrashCanIcon} from '../icons/TrashCanIcon';
import Render from '../render/Render';
import {toggleThemeStyle} from '../theme/helpers';
import {dlsColor, dlsShadow} from '../theme/theme';
import useThemeContext from '../theme/useThemeContext';
import {DLSTheme, IconSize} from '../types/types';
import {BulkImportTemplate} from './BulkImportTemplate';
import BulkUploadImporter from './BulkUploadImporter';
import {BulkUploadResultsPrompt} from './BulkUploadResultsPrompt';
import ImportEmptyIdentifiersValidation from './ImportEmptyIdentifiersValidation';
import ImportProgress from './ImportProgress';
import ImportValidation from './ImportValidation';

// defines the prop definitions for bulk import dialog ...
export type ImportResultsType = {
  result: BulkOperationResults | ExtendedResults;
  valid: boolean;
  isImporting: boolean;
  show: boolean;
  showErrorDialog: boolean;
  isComplete: boolean;
  isError: boolean;
};

// defines the prop definitions for bulk upload dialog ...
export interface BulkUploadDialogType extends DownloadTemplateDeviceTypes {
  dialogTitle?: string | Node;
  open: boolean;
  onClose?: VoidEventHandler;
  onCancel?: VoidEventHandler;
  onImport?: VoidEventHandler;
  onConfirm?: VoidEventHandler;
  onRestart?: VoidEventHandler;
  onDownload?: (params?: DownloadTemplate) => void;
  exportMapper?: Function;
  isLoading?: boolean,
  isDisabled?: boolean,
  styleOverrides?: (theme: DLSTheme) => GenericObject,
  omitActions?: boolean
  entity: Entity;
  entityLabel?: string;
  templateSrcPath?: string;
  templateOptions?: TemplateOptions;
  isOptionsLoading?: boolean;
  itemsUpdate?: Function;
  importResults?: ImportResultsType;
  itemsCount?: number;
  suppressResultsNotification?: boolean;
  clearImportValidation?: () => void;
  uploadFeatureComponent?: ReactNode;
  omitDownloadEntityTemplate?: boolean;
  setBulkImportParams?: Function;
  omitTemplateLinks?: boolean;
  customInstructions?: string;
};

// defines the type definitions for import params ...
export type ImportParamsType = {
  fileName?: string;
  fileSize?: number;
  isComplete?: boolean;
  isFailure?: boolean,
  items?: Array<any>;
}

// defines the styles used by bulk upload dialog ...
const styles = (theme: DLSTheme) => ({
  formPaper : {
    backgroundColor                : toggleThemeStyle(`${dlsColor('grey6')} !important`, dlsColor('white')),
    maxWidth                       : '1067px',
    overflow                       : 'hidden',
    margin                         : 'auto',
    borderRadius                   : toggleThemeStyle(0, 12),
    [theme.breakpoints.down('sm')] : {
      width     : '100%',
      maxHeight : '100% !important',
      margin    : '0px'
    }
  },
  paperScrollPaper : {
    maxHeight                      : 'calc(100% - 94px)',
    minHeight                      : toggleThemeStyle('656px', 'auto'),
    [theme.breakpoints.down('lg')] : {
      minHeight : 'auto'
    }
  },
  scrollPaper : {
    flexDirection  : 'column',
    justifyContent : 'flex-end'
  },
  formDialogTitle : {
    borderBottom : toggleThemeStyle(`solid 1px ${dlsColor('grey4')}`, `solid 1px ${dlsColor('pastelTeal')}`)
  },
  formTitle : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  closeButton : {
    position  : 'absolute',
    left      : '19px',
    top       : '19px',
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  currentCloseButton : {
    position : 'absolute',
    right    : '19px'
  },
  formDialogContent : {
    gap                            : toggleThemeStyle(0, 12),
    margin                         : toggleThemeStyle('auto', 'unset'),
    width                          : toggleThemeStyle('75%', 'auto'),
    [theme.breakpoints.down('lg')] : {
      width : 'fit-content'
    },
    [theme.breakpoints.down('sm')] : {
      paddingTop : '0px'
    }
  },
  formActions : toggleThemeStyle({
    borderTop                      : `solid 1px ${dlsColor('grey4')}`,
    [theme.breakpoints.down('sm')] : {
      justifyContent : 'space-between',
      padding        : '8px 16px'
    }
  }, {
    zIndex    : 1,
    boxShadow : dlsShadow(5.3)
  }),
  primaryButton : toggleThemeStyle({
    width                          : '191px',
    marginRight                    : '28px',
    [theme.breakpoints.down('sm')] : {
      marginRight : '0px'
    }
  }, {
    width               : '68px',
    marginRight         : '28px',
    '& .MuiButton-root' : {
      width      : '85px !important',
      height     : '29px !important',
      fontSize   : '14px',
      fontFamily : 'Inter',
      fontWeight : '500 !important',
      marginLeft : '-10px !important'
    },
    [theme.breakpoints.down('sm')] : {
      marginRight : '0px'
    }
  }),
  uploadStatusContainer : {
    marginTop      : '20px',
    display        : 'flex',
    justifyContent : 'space-between'
  },
  linearProgress : {
    maxWidth : '150px'
  },
  hideUploadStatusContainer : {
    display        : 'none',
    marginTop      : '20px',
    justifyContent : 'space-between'
  },
  fileInfoSection : {
    display : 'flex'
  },
  fileIcon : {
    display     : 'flex',
    marginRight : '12px'
  },
  fileSize : {
    opacity : '0.7'
  },
  chipWrapper : {
    display   : 'flex',
    marginTop : '14px',
    flexWrap  : 'wrap',
    rowGap    : '10px'
  },
  layout : {
    top                            : '10px',
    position                       : 'relative',
    // an upload option's label (re-assigning drivers, say) in Inter ...
    '& .MuiFormControlLabel-label' : {
      fontFamily : 'Inter',
      fontSize   : 14,
      fontWeight : 400
    }
  }
});

/**
 * The BulkUploadDialog provides the option for users to upload files via file system
 * selection or drag/drop to initiate bulk import.
 *
 * ```typescript
 *
 * import {BulkUploadDialog} from '@ion/dls/src/imports/BulkUploadDialog';
 *
 * <BulkUploadDialog open
 *   dialogTitle={translate('common:editors.import.title',
 *     {entity: translate('common:entityLabels.assets')})}
 *   onClose={onClose}
 *   onCancel={onClose}
 *   styleOverrides={overrides}
 *   onImport={handleSubmit}
 *   onDownload={handleDownload}
 *   onConfirm={confirm}
 *   onRestart={handleRestart}
 *   entity={Entity.asset}
 *   entityLabel={translate('common:entityLabels.assets')}
 *   templateSrcPath={'admin-assets'}
 *   templateOptions={templateOptions}
 *   isLoading={false}
 *   itemsUpdate={itemsUpdate}
 *   isDisabled={false}
 *   omitActions={false}
 *   importResults={importResults} />
 * ```
 *
 * @param dialogTitle Dialog title
 * @param open should the dialog be open
 * @param onClose callback when the dialog requests to be closed
 * @param onCancel callback when user clicks the cancel button
 * @param onImport callback when user clicks the import button
 * @param onDownload callback when user clicks the download pre-populated template button
 * @param onConfirm callback to execute when import results are generated and user confirms
 * the same
 * @param itemsUpdate callback to be executed post successful import
 * @param onRestart callback to execute when user wants to start a fresh import
 * @param entity The current CTCEntity under bulk import
 * @param entityLabel The current CTCEntity label under bulk import
 * @param templateSrcPath The path to the template source file
 * @param templateOptions The pre-defines values for excel columns
 * @param importResults Params post import which helps in processing results and
 * errors to be shown to user and for generating excel data for the same
 * @param isLoading checks if upload is completed to prevent user from clicking import until
 * upload is completed
 * @param omitActions checks if dailog actions can be omitted
 * @param styleOverrides function that returns styles to override the base
 * component styles
 */
export const BulkUploadDialog = ({
  dialogTitle,
  open,
  onClose,
  onCancel,
  onImport,
  onDownload,
  onConfirm,
  onRestart,
  exportMapper,
  styleOverrides,
  entity,
  entityLabel,
  templateSrcPath,
  templateOptions,
  isOptionsLoading,
  itemsUpdate,
  importResults,
  clearImportValidation,
  uploadFeatureComponent,
  isLoading = false,
  isDisabled = false,
  omitActions = false,
  isValidFile = false,
  itemsCount = 0,
  suppressResultsNotification = false,
  isDeviceImport = false,
  omitTemplateLinks = false,
  omitDownloadEntityTemplate,
  setBulkImportParams,
  customInstructions = ''
}: BulkUploadDialogType) => {
  const dialogStyles                            = useExtendStyles(styles, styleOverrides);
  const classes                                 = dialogStyles();
  const isMounted                               = useHasMounted();
  const id                                      = `${entity}-importer`;
  const [importStatus, setImportStatus]         = useState(false);
  const [importComplete, setImportComplete]     = useState(false);
  const [importValidation, setImportValidation] = useState([]);
  const [validateVision, setValidateVision]     = useState([]);
  const [importerOptions, setImporterOptions]   = useState<DropsheetOptions>();
  const [importParams, setImportParams]         = useState<ImportParamsType>();
  const templateColumns: TemplateColumn[]       = templateSrcPath ? translate(`${templateSrcPath}:template`, {returnObjects: true}) || [] : [];
  const attributes                              = !omitTemplateLinks ? idMap(templateColumns, 'header', 'key') : {};
  const headers                                 = Object.keys(attributes);
  const isDeviceModule                          = entity === Entity.device;
  const isImportStatus                          = isDeviceModule
    ? isEmpty(importValidation) && isEmpty(validateVision) : true;
  const path                                    = 'admin-devices:import.emptyIdentifiersMessage';

  const {
    isCurrent : isCurrentTheme,
    isLegacy  : isLegacyTheme
  } = useThemeContext();

  const {
    result,
    valid,
    isImporting,
    show,
    showErrorDialog,
    isComplete,
    isError
  } = importResults || {};

  /**
   * @private
   */
  const onImportCompleted = function(
    items: Array<any>,
    workbook: WorkBook,
    files: Array<any>
  ) {
    const file          = files ? files[0] : {};
    let errorCheck      = false;
    if (templateColumns?.length) {
      const mandatoryCols = filter(templateColumns, {required: true});
      const headers       = Array.isArray(items[0]) ? items[0] : null;
      const dataRows      = headers ? items.slice(1) : items;

      for (const item of dataRows) {
        for (const col of mandatoryCols) {
          const colIndex = headers ? headers.indexOf(col.key) : null;
          const value    = headers ? item[colIndex] : item[col.key];
          if (isNil(value)) {
            errorCheck = true;
            break;
          }
        }
        if (errorCheck) break;
      }
    }
    if (errorCheck) return onImportFailed();

    if (isDeviceModule) {
      const {isValidImport, invalidVisionDevices} = validateEsn(items);
      if (isValidImport?.[0]?.isValidEsn) {
        const deviceEsn = isValidImport?.map(({deviceEsn}) => deviceEsn);
        setImportValidation(deviceEsn);
      } else {
        setImportValidation([]);
      }
      if (invalidVisionDevices?.[0]?.isInValidEsn) {
        const deviceName = invalidVisionDevices?.map(({deviceName}) => deviceName);
        setValidateVision(deviceName);
      } else {
        setValidateVision([]);
      }
    }

    setImportParams({
      ...importParams,
      fileName   : file?.name,
      fileSize   : file?.size,
      isComplete : true,
      isFailure  : false,
      items
    });
    itemsUpdate?.(items);
    setBulkImportParams?.(file);
  };

  const validateEsn = (item: GenericObject[]) => {
    const importValidation = [];
    const invalidVisionDevices = [];
    item?.forEach((device) => {
      const {esn, imei, name, type} = device;
      if (isNaN(Number(esn)) && !isEmpty(esn) && !isUndefined(esn)) {
        importValidation.push({
          isValidEsn : true,
          deviceEsn  : esn
        });
      }
      if (isEqual(type.toUpperCase(), VisionDeviceType) && isStringEmpty(imei?.toString())) {
        invalidVisionDevices.push({
          isInValidEsn : true,
          deviceName   : name
        });
      }
    });
    return {isValidImport: importValidation, invalidVisionDevices};
  };

  /**
   * @private
   */
  const onImportFailed = function(e?: unknown) {
    clearImport();
    setImportParams({
      ...importParams,
      isComplete : false,
      isFailure  : true
    });
  };

  /**
   * Converts the mappings of Spreadsheet column titles to CTC attributes.
   *
   * @private
   */
  const converter = (column: string): string => {
    const attrs   = normalizeAttributes(attributes);
    const extract = isDeviceModule ? column !== headers[5] : true;
    const header  = normalizeHeaders(column, attrs, extract);
    return isDeviceModule ? header || column : header;
  };

  /**
   * @private
   */
  function handleImport(files: FileList) {
    setImportStatus(true);
    process(files, importerOptions, isDeviceModule);
  };

  /**
   * @onExport
   */
  const onExport = function(errorsOnly?: boolean) {
    const {result} = importResults;
    const results  = exportMapper?.(result, errorsOnly, attributes);
    const columns  = [...headers].concat([translate('common:editors.import.results.status'),
      translate('common:editors.import.results.errors')]);
    const fileName = `${entityLabel}-${translate(`common:editors.import.results.${errorsOnly
      ? 'errors' : 'name'}`)}`.toLowerCase();
    const downloader = exporter({
      title   : entityLabel,
      headers : columns,
      data    : results,
      options : templateOptions || {
        titles     : true,
        appendDate : true,
        immediate  : false
      },
      fileName
    });
    downloader?.();
  };

  /**
   * @initializeImportParams
   */
  const initializeImportParams = function() {
    setImportParams({
      items      : [],
      isComplete : false,
      isFailure  : false
    });
  };

  /**
   * @clearImport
   */
  const clearImport = function() {
    const importerId = `${entity}-import`;
    const importContainer = document.getElementById(importerId) as HTMLInputElement;
    if (importContainer) {
      importContainer.value = null;
    }
    setImportStatus(false);
    initializeImportParams();
    setImportValidation([]);
    setValidateVision([]);
  };

  /**
   * @initializeImporter
   */
  const initializeImporterOptions = () => {
    setImporterOptions({
      onComplete       : onImportCompleted,
      onFailed         : onImportFailed,
      onSizeError      : onImportFailed,
      convertCSVToJSON : isValidFile,
      parse            : !isValidFile,
      converter
    });
  };

  useEffect(() => {
    if (!isMounted) {
      initializeImporterOptions();
      initializeImportParams();
    } else {
      importerOptions.target = document.getElementById(id);
      dropsheet(importerOptions, setImportStatus, isDeviceModule);
    }
  }, [isMounted]);

  useEffect(() => {
    setImportComplete(importStatus && importParams?.isComplete);
  }, [importStatus, importParams?.isComplete]);

  const length = importParams?.items?.length;
  const size   = isFunction(length?.toLocaleString) ? length?.toLocaleString() : length?.toString();

  function formatBytes(bytes, decimals = 2) {
    if (typeof bytes === 'undefined') return '';

    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  function onNewImport(e: Event) {
    onRestart(e);
    clearImport();
  };

  return (
    <>
      {!show && (
        <Dialog open={open}
          classes={getClasses(classes)}>
          <DialogTitle classes={{
            root : classes.formDialogTitle
          }}>
            <div className={classes.formTitle}>
              <Render.If condition={isLegacyTheme}>
                <div onClick={onClose} className={classes.closeButton}>
                  <MaterialExitCloseIcon size={IconSize.medium}
                    color={dlsColor('grey3')} />
                </div>
              </Render.If>
              {dialogTitle && (
                <Typography component='h1' variant='h2'>
                  {dialogTitle}
                </Typography>
              )}
              <Render.If condition={isCurrentTheme}>
                <div className={classes.currentCloseButton}>
                  <CloseButton onClick={onClose} />
                </div>
              </Render.If>
            </div>
          </DialogTitle>
          <DialogContent className={classes.formDialogContent}>
            <BulkUploadImporter id={id}
              size={size}
              uploadTitle={translate('common:editors.import.uploadTitle',
                {entity: entityLabel})}
              onImport={handleImport}
              isCurrentTheme={isCurrentTheme}
              entity={entity}
              omitInstructions={omitTemplateLinks}
              customInstructions={customInstructions}
              entityLabel={entityLabel}
              importParams={importParams} />
            <Render.If condition={!omitTemplateLinks}>
              <BulkImportTemplate entity={entity}
                entityLabel={entityLabel}
                isDeviceImport={isDeviceImport}
                isValidFile={isValidFile}
                isCurrentTheme={isCurrentTheme}
                templateColumns={templateColumns}
                templateOptions={templateOptions}
                isOptionsLoading={isOptionsLoading}
                onDownload={onDownload}
                omitDownloadEntityTemplate={omitDownloadEntityTemplate} />
            </Render.If>
            <div className={isCurrentTheme ? classes.layout : ''}>
              {uploadFeatureComponent}
            </div>
            {isImportStatus && (
              <div id='upload-status' className={classnames(importStatus
                ? classes.uploadStatusContainer : classes.hideUploadStatusContainer)}>
                <div className={classes.fileInfoSection}>
                  <div className={classes.fileIcon}><FileIcon /></div>
                  <div>
                    <ImportProgress isCompleted={importComplete}
                      text={importParams?.fileName || ''} />
                  </div>
                </div>
                <div className={classes.fileSize}>
                  <ImportProgress isCompleted={importComplete}
                    text={formatBytes(importParams?.fileSize) || ''} />
                </div>
                <div className={classes.linearProgress}>
                  <Grid xs item>
                    {importStatus ? importParams.isComplete ? translate('common:editors.import.uploadCompleted') : translate('common:editors.import.uploadProgress') : ''}
                  </Grid>
                  <Grid xs item>
                    {importStatus ? importParams.isComplete ? <LinearProgress variant='determinate' value={100} /> : <LinearProgress /> : ''}
                  </Grid>
                </div>
                <IconButton style={{padding: '0px'}} onClick={clearImport} icon={<TrashCanIcon />} />
              </div>
            )}
            {isDeviceModule && (
              <><div className={classes.chipWrapper}>
                {(importValidation?.length && importParams?.isComplete)
                  ? <ImportValidation importValidation={importValidation} />
                  : ''}
              </div><div className={classes.chipWrapper}>
                {(validateVision?.length && importParams?.isComplete)
                  ? <ImportValidation isVision importValidation={validateVision} />
                  : ''}
              </div></>
            )}
            {isDeviceModule && showErrorDialog && (
              <ImportEmptyIdentifiersValidation title={translate(`${path}.title`)}
                message={translate(`${path}.message`)}
                actionLabel={translate(`${path}.actionLabel`)}
                onAction={clearImportValidation} />
            )}
          </DialogContent>
          {(!omitActions && (onCancel || onImport)) && (
            <DialogActions className={classes.formActions}>
              {onCancel && <CancelButton onCancel={onCancel} />}
              {onImport && (
                <div className={classes.primaryButton}>
                  <SaveButton onSave={onImport}
                    label={translate('common:editors.import.button.label')}
                    loading={isLoading}
                    disabled={isLoading ||
                    !importParams?.isComplete ||
                    !isEmpty(importValidation) ||
                    !isEmpty(validateVision)} />
                </div>
              )}
            </DialogActions>
          )}
        </Dialog>
      )}
      <BulkUploadResultsPrompt entity={entity}
        entityLabel={entityLabel}
        result={result}
        valid={valid}
        isImporting={isImporting}
        show={show}
        isComplete={isComplete}
        isError={isError}
        onExport={onExport}
        onConfirm={onConfirm}
        onRestart={onNewImport}
        itemsCount={itemsCount}
        suppressResultsNotification={suppressResultsNotification} />
    </>
  );
};

export default BulkUploadDialog;
